import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Course } from '@/types'

import { BookOpen } from 'lucide-react'
import { getCourses } from '@/lib/courses'
import { loadPageData, REVALIDATE_SECONDS, RETRY_REVALIDATE_SECONDS } from '@/lib/page-data'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { Layout } from '@/components/generals/layout'
import { SectionHeading } from '@/components/generals/section-heading'
import { Seo } from '@/components/generals/seo'
import { CourseCatalog } from '@/components/courses/course-catalog'
import { CtaSection } from '@/components/landing/cta-section'

interface CoursesPageProps {
    courses: Array<Course>
}

const CoursesPage: FC<CoursesPageProps> = ({ courses }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Courses"
                description="Browse practical computer courses for every level: computer basics, Microsoft Office, Excel, graphic design, web development and Python. Small classes, in person or live online."
            />

            <section id='catalog-intro' className='relative overflow-hidden border-b bg-secondary/40'>
                <div
                    aria-hidden='true'
                    className='bg-dots pointer-events-none absolute inset-0 text-foreground/40 [mask-image:radial-gradient(ellipse_70%_80%_at_0%_0%,black,transparent)]'
                />

                <Container className='relative py-14 sm:py-20'>
                    <SectionHeading
                        as='h1'
                        eyebrow="Course catalog"
                        title="Courses for every level"
                        description="Hands-on computer courses taught in small classes, in our classroom or live online. Filter by level and topic to find your fit."
                    />
                </Container>
            </section>

            <section id='catalog' aria-label="Course list" className='py-12 sm:py-16'>
                <Container>
                    {courses.length > 0
                        ? <CourseCatalog courses={courses} />
                        : (
                            <div className='flex flex-col items-center rounded-3xl border border-dashed bg-card px-6 py-16 text-center'>
                                <span className='flex size-12 items-center justify-center rounded-2xl bg-secondary text-primary'>
                                    <BookOpen className='size-6' aria-hidden='true' />
                                </span>

                                <h2 className='mt-5 text-xl font-semibold'>
                                    New courses are on their way
                                </h2>

                                <p className='mt-2 max-w-md text-muted-foreground'>
                                    We are updating our course list. Send us a message and an advisor will tell you what is starting next.
                                </p>

                                <ButtonLink href='/enroll' className='mt-6'>
                                    Ask an advisor
                                </ButtonLink>
                            </div>
                        )}
                </Container>
            </section>

            <CtaSection
                eyebrow="Not sure where to start?"
                title="Tell us your goal and we will suggest a course."
                description="Share a little about your experience and what you want to achieve. An advisor will recommend the right starting point within one working day, with no commitment."
                primary={{ label: "Get a recommendation", href: '/enroll' }}
                secondary={{ label: "Read the FAQ", href: '/#faq' }}
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<CoursesPageProps> = async () => {
    const courses = await loadPageData('courses', getCourses, [])

    return {
        props: {
            courses: courses.data
        },
        revalidate: courses.failed ? RETRY_REVALIDATE_SECONDS : REVALIDATE_SECONDS
    }
}

export default CoursesPage
